import differenceInDays from "date-fns/differenceInDays"
import isPast from "date-fns/isPast"
import parseISO from "date-fns/parseISO"
import camelCase from "lodash/camelCase"
import deburr from "lodash/deburr"
import DEFAULT_I18N from "~/i18n"
import {
    ACTIVITY_PASS_MODES,
    ACTIVITY_TYPES,
    CALL_STATUSES,
    SCORM_CHAPTER_STATUSES,
    SUPPORTED_ACTIVITY_TYPES,
} from "~/lib/constants"
import {
    fmtTimeHourMin,
    fmtveryshortdate,
    fmtwordsago,
    ucfirst,
} from "~/lib/utils"

/**
 * CALL utilities
 */

export function cookActivityNodes({
    nodes,
    level = 1,
    parentTitle,
    queryString = "",
}) {
    const qs = deburr(queryString).toLowerCase()
    for (let [i, node] of nodes.entries()) {
        if (node.level) {
            // set block title
            if (level == 1) {
                node.roundedTop = true
                // A level may be empty
                const nextNodeIsLevel = node.activities[0]?.level
                node.hasSublevel = nextNodeIsLevel
            }
            if (level >= 3) {
                node.title = `${parentTitle} / ${node.title}`
            }
            cookActivityNodes({
                nodes: node.activities,
                level: level + 1,
                parentTitle: node.title,
                queryString,
            })
        } else {
            const prev = nodes[i - 1]
            const next = nodes[i + 1]

            // connect contiguous blocked activities
            if (prev && !prev.level && node.blocked) {
                node.topConnected = true
            }

            if (next && next.blocked) {
                node.bottomConnected = true
            }

            if (!prev && level == 1) {
                node.roundedTop = true
            }

            if (!next) {
                node.roundedBottom = true
            }

            if (!deburr(node.title.toLowerCase()).includes(qs)) {
                node.hidden = true
            } else {
                node.hidden = false
            }
        }
    }
    return nodes
}

export function getCallStatusColor(status) {
    status = status.toLowerCase()
    switch (status) {
        case "coursing":
            return "warning"
        case "unevaluated":
            return "info"
        case "succeeded":
            return "success"
        case "pending":
        case "expired":
        case "cancelled":
            return "gray-dark"
        case "failed":
        case "unrealized":
            return "danger"
        default:
            throw new Error(`Unknown call status: ${status}`)
    }
}

export function getCallStatusIcon(status) {
    status = status.toLowerCase()
    switch (status) {
        case "coursing":
        case "pending":
            return "coursing"
        case "failed":
            return "failed"
        case "succeeded":
            return "approved"
        case "unevaluated":
            return "unevaluated"
        case "unrealized":
        case "cancelled":
        case "expired":
            return "unrealized"
        default:
            throw new Error(`Unknown call status: ${status}`)
    }
}

export function getFirstPlayableActivity(activities) {
    let firstPlayableActivity
    const isPlayable = activity => {
        if (
            !activity.level &&
            !isFinished(activity) &&
            !activity.blocked &&
            !activity.validatingUserActivityId &&
            isActivityImplemented(activity.type)
        ) {
            return true
        }
    }
    const hasPlayableActivity = a => {
        if (isPlayable(a)) {
            firstPlayableActivity = a
            return true
        }
        return (
            Array.isArray(a.activities) &&
            a.activities.some(hasPlayableActivity)
        )
    }
    activities.some(hasPlayableActivity)

    return firstPlayableActivity
}

export function getLastPlayedActivity(navigation) {
    const lastNavigationFromPlugin = navigation
        .filter(({ from }) => from.name == "plugin")
        .at(-1)
    if (!lastNavigationFromPlugin) return null
    const lastUserActivityId =
        lastNavigationFromPlugin.from.params?.userActivityId
    return Number(lastUserActivityId)
}

export function translateCallStatus(t, status) {
    if (!CALL_STATUSES.includes(ucfirst(status.toLowerCase()))) {
        throw new Error(`Unknown call status: ${status}`)
    }
    return t(`common.${camelCase(status.toLowerCase())}`, 1)
}

/**
 * ACTIVITY utilities
 */

/**
 * @param activity Object
 * @param extendedVersion Boolean
 * @param locale String
 * @param timeZone String
 * @param t Function
 * @param normalClass Array<String>
 * @param urgentClass Array<String>
 * @returns { text: String, klass: Array<String> }
 */
export function cookActivityDisplayDate({
    activity,
    extendedVersion = false,
    locale,
    now = new Date().toISOString(),
    timeZone,
    t,
    normalClass = ["text-gray-dark", "font-weight-semi-bold"],
    urgentClass = [
        "border",
        "border-warning",
        "font-weight-semi-bold",
        "px-8",
        "rounded-sm",
        "text-dark",
    ],
}) {
    let { fromDate, toDate } = activity

    now = parseISO(now)
    fromDate = parseISO(fromDate)
    toDate = parseISO(toDate)
    const daysToStart = differenceInDays(fromDate, now)
    const daysToFinish = differenceInDays(toDate, now)
    const isFinished = toDate < now
    const isStarted = fromDate < now

    if (!isStarted) {
        if (daysToStart > 30) {
            let text = ucfirst(
                t("common.startsOn", {
                    date: fmtveryshortdate(locale, timeZone, fromDate),
                })
            )
            if (extendedVersion) {
                text += ` - ${ucfirst(t("common.dueOn", { date: fmtveryshortdate(locale, timeZone, toDate) }))}`
            }
            return {
                text,
                klass: normalClass,
            }
        } else {
            if (extendedVersion) {
                let text = `${ucfirst(t("common.startsIn", { time: fmtwordsago(locale, fromDate, now) }))}: ${fmtveryshortdate(locale, timeZone, fromDate)}`
                text += ` - ${ucfirst(t("common.dueOn", { date: fmtveryshortdate(locale, timeZone, toDate) }))}`
                return {
                    text,
                    klass: normalClass,
                }
            }
            return {
                text: ucfirst(
                    t("common.startsOn", {
                        date: fmtveryshortdate(locale, timeZone, fromDate),
                    })
                ),
                klass: normalClass,
            }
        }
    }

    if (!isFinished) {
        if (daysToFinish > 30) {
            return {
                text: ucfirst(
                    t("common.dueOn", {
                        date: fmtveryshortdate(locale, timeZone, toDate),
                    })
                ),
                klass: normalClass,
            }
        }

        if (daysToFinish > 10) {
            if (extendedVersion) {
                return {
                    text: `${ucfirst(t("common.dueIn", { time: fmtwordsago(locale, toDate, now) }))}`,
                    klass: normalClass,
                }
            }
            return {
                text: ucfirst(
                    t("common.dueOn", {
                        date: fmtveryshortdate(locale, timeZone, toDate),
                    })
                ),
                klass: normalClass,
            }
        }

        if (daysToFinish >= 0) {
            if (extendedVersion) {
                return {
                    text: `${ucfirst(t("common.dueIn", { time: fmtwordsago(locale, toDate, now) }))}`,
                    klass: urgentClass,
                }
            }
            return {
                text: ucfirst(
                    t("common.dueOn", {
                        date: fmtveryshortdate(locale, timeZone, toDate),
                    })
                ),
                klass: urgentClass,
            }
        }
    }

    const startText = ucfirst(
        t("common.started", {
            date: fmtveryshortdate(locale, timeZone, fromDate),
        })
    )
    const endText = ucfirst(
        t("common.ended", { date: fmtveryshortdate(locale, timeZone, toDate) })
    )
    const text = extendedVersion ? `${startText} - ${endText}` : endText
    return {
        text,
        klass: normalClass,
    }
}

/**
 * @param activity Object
 * @param isDesktop Boolean
 * @param isCarousel Boolean
 * @param i18n Object - used only to be able to stub this part in the tests
 */
export function getActivityAttributes({
    activity,
    isDesktop,
    isCarousel,
    i18n,
}) {
    i18n = i18n || DEFAULT_I18N.global
    const credits = isDesktop
        ? `${activity.credits}/${activity.maxCredits}`
        : `${activity.credits}`
    return [
        isCarousel && {
            icon: "entry",
            label: ucfirst(i18n.t("common.accesses", activity.attempts)),
            value: activity.attempts,
        },
        isCarousel &&
            activity.experiencedTime && {
                icon: "clock",
                label: ucfirst(i18n.t("common.experiencedTime")),
                value: fmtTimeHourMin(activity.experiencedTime),
            },
        {
            icon: "medal",
            label: ucfirst(i18n.t("common.qualification", 1)),
            value: activity.hasQualification ? activity.qualification : "-",
        },
        activity.maxCredits > 0 && {
            icon: "trophy",
            label: ucfirst(i18n.t("common.credits", 2)),
            value: credits,
        },
    ].filter(Boolean)
}

export function getActivityActionIcon(activity) {
    const { status, blocked, validatingUserActivityId } = activity

    if (validatingUserActivityId) {
        return {
            name: "award",
            class: "text-success",
        }
    }

    if (blocked) {
        return {
            name: "lock",
            class: "text-dark",
        }
    }

    switch (status.toLowerCase()) {
        case "pending":
            return {
                name: "play",
                class: "text-gray-dark",
            }
        case "coursing":
            return {
                name: "play",
                class: "text-warning",
            }

        case "failed":
            return {
                name: "failed",
                class: "text-danger",
            }

        case "succeeded":
            return {
                name: "approved",
                class: "text-success",
            }

        case "unevaluated":
            return {
                name: "unevaluated",
                class: "text-gray-dark",
            }

        case "unrealized":
        case "cancelled":
        case "expired":
            return {
                name: "unrealized",
                class: "text-danger",
            }

        default:
            throw new Error(`Unknown activity status: ${status}`)
    }
}

export function getActivityIcon(type) {
    switch (type.toUpperCase()) {
        case "SCORM_VIDEO":
            return "presentation"

        case "OFFLINE_SESSIONS":
            return "map"

        case "POLLS":
            return "file-edit-alt"

        case "CUSTOM_POLL_V2":
        case "SATISFACTION_POLL_V2":
            return "file-edit-alt"

        case "SCORM": // SCORM
            return "meeting"

        case "SOCIAL_VIDEO":
            return "youtube"

        case "TNT":
        case "TASKS":
            return "plugin-tnt"

        case "UNIVERSAL_CONECTOR":
            return "crosshair"

        case "EXAMS":
            return "edit"

        case "WORKGROUPS":
            return "users-alt"

        case "JOB_TRAINING":
            return "notes"

        case "PERFORMANCE_EVALUATION":
            return "chart-line"

        case "OBJECTIVES":
            return "crosshair"

        case "VIRTUAL_CLASSROOM":
            return "webcam"

        case "EXTERNAL_SIMULATOR_CIRCULATION":
        case "SIMULATOR":
            return "external-link"

        default:
            return "file-exclamation-alt"
    }
}

export function getActivityLocation(activity) {
    if (
        activity.blocked ||
        hasExpired(activity) ||
        !isActivityImplemented(activity.type)
    ) {
        return `/call/${activity.userCallId}?activity=${activity.userActivityId}`
    }
    return `/plugin/${activity.userActivityId}`
}

/**
 * @param activity Object
 * @param t Function
 * @returns { statusText, statusTextClass, statusIcon, statusIconClass } Object
 */
export function getActivityStatusAndIcon({ activity, t }) {
    const { status, validatingUserActivityId } = activity

    if (validatingUserActivityId) {
        return {
            color: "success",
            icon: "approved",
            text: t("common.validated"),
            textClass: "text-success",
            iconClass: "text-success",
        }
    }

    const lowStatus = status.toLowerCase()
    const text = t(`common.${lowStatus}`, 1)

    switch (lowStatus) {
        case "pending":
            return {
                color: "dark",
                icon: "coursing",
                text,
                textClass: "text-dark",
                iconClass: "text-gray-dark",
            }

        case "coursing":
            return {
                color: "warning",
                icon: "coursing",
                text,
                textClass: "text-warning",
                iconClass: "text-warning",
            }

        case "failed":
            return {
                color: "danger",
                icon: "failed",
                text,
                textClass: "text-danger",
                iconClass: "text-danger",
            }
        case "succeeded":
            return {
                color: "success",
                icon: "approved",
                text,
                textClass: "text-success",
                iconClass: "text-success",
            }

        case "unevaluated":
            return {
                color: "gray-dark",
                icon: "unevaluated",
                text,
                textClass: "text-gray-dark",
                iconClass: "text-gray-dark",
            }

        case "unrealized":
        case "cancelled":
        case "expired":
            return {
                color: "danger",
                icon: "unrealized",
                text,
                textClass: "text-danger",
                iconClass: "text-danger",
            }

        default:
            throw new Error(`Unknown activity status: ${status}`)
    }
}

export function getActivityStatusColor(status) {
    status = status.toLowerCase()
    switch (status) {
        case "pending":
            return "dark"
        case "coursing":
            return "warning"
        case "unevaluated":
            return "gray-dark"
        case "succeeded":
            return "success"
        case "expired":
        case "unrealized":
        case "failed":
        case "cancelled":
            return "danger"
        default:
            throw new Error(`Unknown activity status: ${status}`)
    }
}

export function getActivityStatusIcon(activity) {
    if (activity.blocked) return "lock"

    if (activity.type === "OFFLINE_SESSIONS") {
        const { stripFromDate } = activity
        if (!stripFromDate) return "eye-full"
    }

    const status = activity.status.toLowerCase()
    switch (status) {
        case "pending":
        case "coursing":
            return "play"

        case "expired":
        case "unevaluated":
        case "unrealized":
        case "succeeded":
        case "failed":
        case "cancelled":
            return "eye-full"

        default:
            throw new Error(`Unknown activity status: ${status}`)
    }
}

export function hasExpired(activity) {
    const { toDate } = activity
    return isPast(parseISO(toDate))
}

export function isActivityImplemented(type) {
    return SUPPORTED_ACTIVITY_TYPES.includes(type.toUpperCase())
}

export function isFinished({ status }) {
    // It works for both calls and activities
    const cookedStatus = status.toLowerCase()
    return !["pending", "coursing", "unevaluated"].includes(cookedStatus)
}

export function translateActivityPassMode(
    t,
    passMode,
    passProgress,
    passQualification
) {
    passMode = passMode.toLowerCase()
    if (!ACTIVITY_PASS_MODES.includes(passMode)) {
        throw new Error(`Unknown pass mode ${passMode}`)
    }

    switch (passMode) {
        case "access": {
            return ucfirst(t(`common.access`))
        }

        case "complete": {
            const complete = ucfirst(t(`common.complete`))
            return `${complete} ${passProgress}%`
        }

        case "pass": {
            const pass = ucfirst(t(`common.pass`))
            return `${pass} ${passQualification} pts.`
        }

        case "pass_and_complete": {
            const pass = ucfirst(t(`common.pass`))
            const and = t(`common.and`)
            const complete = t(`common.complete`)

            return `${pass} ${passQualification} pts. ${and} ${complete} ${passProgress}%`
        }

        default:
            throw new Error(`Unknown pass mode ${passMode}`)
    }
}

export function translateActivityType(t, type) {
    if (!ACTIVITY_TYPES.includes(type)) {
        throw new Error(`Unknown activity type: ${type}`)
    }
    return t(`common.${camelCase(type)}`, 1)
}

export function translateScormChapterStatus(t, status) {
    if (!SCORM_CHAPTER_STATUSES.includes(status)) {
        throw new Error(`Unknown scorm chapter status: ${status}`)
    }
    return t(`common.${camelCase(status.toLowerCase())}`, 1)
}
